import React from 'react'
import { SimpleChart } from './charts'
import CapacityInfo from './CapacityInfo'
import { getTargetAnnotation } from '../../utils/targetLines'
import { CAN_BUS_STYLES } from '../../utils/styleUtils'

/**
 * Measurement icon mapping for visual enhancement
 */
const getMeasurementIcon = (measurement) => {
  const iconMap = {
    volts: 'bi-lightning',          // Voltage
    amps: 'bi-arrow-up-right',      // Current/Amps
    temp: 'bi-thermometer-half',    // Temperature
    field: 'bi-sliders',            // Field percentage
    motorRpm: 'bi-speedometer2',    // RPM
    soc: 'bi-battery-charging',     // State of Charge
    engineLoad: 'bi-cpu',            // Engine Load
  }
  return iconMap[measurement] || 'bi-graph-up' // Default icon
}

/**
 * Measurement color mapping based on type
 */
const getMeasurementColor = (measurement, value) => {
  // Dynamic coloring based on measurement type and value
  switch (measurement) {
    case 'volts':
      return value > 14 ? 'text-success' : value > 12 ? 'text-warning' : 'text-danger'
    case 'temp':
      return value > 70 ? 'text-danger' : value > 50 ? 'text-warning' : 'text-success'
    case 'soc':
      return value > 80 ? 'text-success' : value > 50 ? 'text-warning' : 'text-danger'
    case 'amps':
    case 'field':
    case 'motorRpm':
    case 'engineLoad':
    default:
      return 'text-primary'
  }
}

/**
 * MetricCard component that displays individual measurement data with real-time charts
 * Enhanced in Phase 4 to include TimeSeriesChart and RadialGauge components
 * Enhanced in Phase 5 to support dynamic chart visibility and capacity calculations
 * Enhanced to include CAN bus source indicators in tile headers
 * Optimized with React.memo to prevent unnecessary re-renders
 */
function MetricCard({ 
  _device, 
  measurement, 
  value, 
  _timestamp, 
  unit = '', 
  chartData = [], 
  _canBusData = null,
  // Phase 5: Chart visibility props
  showChart = true,
  hasRecentData = true,
  // Phase 5 Task 3: Capacity calculation props
  realTimeData = null,
  // Fault data for chart coloring
  _faults = [],  // Fault data (not used for chart coloring)
  // Device border color for visual consistency
  deviceBorderColor = '#dee2e6',
  // CAN bus source detection
  dataSource = null,
}) {
  // Format the measurement name for display
  const formatMeasurementName = (measurement) => {
    const formatMap = {
      volts: 'Voltage',
      amps: 'Current', 
      temp: 'Temperature',
      field: 'Field',
      motorRpm: 'Motor RPM',
      soc: 'State of Charge',
      engineLoad: 'Engine Load',
    }
    return formatMap[measurement] || measurement
  }

  // Format the value for display
  const formatValue = (value, measurement) => {
    if (value === null || value === undefined) {
      return '--'
    }

    switch (measurement) {
      case 'volts':
        return `${Number(value).toFixed(1)}V`
      case 'amps':
        return `${Number(value).toFixed(1)}A`
      case 'temp':
        return `${Number(value).toFixed(1)}${unit}`;      case 'field':
        return `${Number(value).toFixed(0)}%`
      case 'soc':
        return `${Number(value).toFixed(0)}%`
      case 'motorRpm':
        return `${Number(value).toFixed(0)} RPM`
      case 'engineLoad':
        return `${Number(value).toFixed(0)}%`
      default:
        return String(value)
    }  }
  
  // Phase 5: Enhanced chart rendering with visibility logic and target lines
  const renderChart = () => {
    // Don't render chart if no data points available
    if (!chartData || chartData.length === 0) {
      return (
        <div className="mt-2 flex-grow-1 d-flex align-items-center justify-content-center text-muted">
          <small>
            <i className="bi bi-graph-up me-1"></i>
            {!showChart ? 'Chart hidden by visibility logic' : 
             !hasRecentData ? (
               <span className="d-flex align-items-center">
                 <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                 Waiting for recent data...
               </span>
             ) : 
             'No chart data'}
          </small>
        </div>
      )
    }

    // Calculate target annotation for this measurement
    const targetAnnotation = getTargetAnnotation(_device, measurement, realTimeData)

    // Show the chart if we have data and visibility conditions are met
    return (
      <div className="mt-2 flex-grow-1">
        <SimpleChart 
          data={chartData} 
          measurement={measurement}
          unit={unit}
          height={120}  // Much larger height to fill the card
          showGrid={false}
          showAxes={false}
          targetAnnotation={targetAnnotation}            _dataSource={dataSource}
        />
      </div>
    )
  }
  const displayName = formatMeasurementName(measurement)
  const displayValue = formatValue(value, measurement)
  const measurementIcon = getMeasurementIcon(measurement)
  const valueColor = getMeasurementColor(measurement, value)

  // Data freshness indicator
  const getDataFreshness = () => {
    if (!_timestamp) return 'unknown'
    const now = Date.now()
    const dataTime = new Date(_timestamp).getTime()
    const ageSeconds = (now - dataTime) / 1000
    
    if (ageSeconds < 5) return 'fresh'
    if (ageSeconds < 30) return 'recent'
    return 'stale'
  }

  const freshness = getDataFreshness()

  return (
    <div 
      className="card h-100 metric-card"
      data-fresh={freshness === 'fresh'}
      data-stale={freshness === 'stale'}
      style={{
        transition: 'all 0.3s ease',
        cursor: 'default',
        borderLeft: `4px solid ${deviceBorderColor}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <div className="card-header bg-light border-0 py-1">
        <div className="d-flex align-items-center justify-content-between" style={{ minHeight: '1.5rem' }}>
          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
            {/* Main header row with icon and title */}
            <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
              <i className={`${measurementIcon} me-1 flex-shrink-0`} style={{ fontSize: '0.9rem' }}></i>
              <span 
                className="text-muted fw-medium"
                title={displayName}
                style={{ 
                  fontSize: '1.1rem',
                  lineHeight: '1.3',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </span>
            </div>
            {/* CAN bus source indicator on separate line */}
            {dataSource === 'canbus' && (
              <div className="mt-1">
                <span 
                  className="badge bg-primary"
                  style={{
                    ...CAN_BUS_STYLES.badge,
                    backgroundColor: CAN_BUS_STYLES.badgeColor,
                    fontSize: '0.75rem',
                  }}
                  title="Data source: CAN bus"
                >
                  CAN bus
                </span>
              </div>
            )}
          </div>
          {/* Data freshness indicator */}
          <span 
            className="badge badge-sm flex-shrink-0 ms-1"
            style={{
              backgroundColor: freshness === 'fresh' ? '#28a745' : 
                              freshness === 'recent' ? '#ffc107' : '#dc3545',
              color: 'white',
              fontSize: '0.6rem',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              padding: 0,
            }}
            title={`Data ${freshness}`}
          ></span>
        </div>
      </div>

      <div className="card-body d-flex flex-column justify-content-center text-center px-2 py-2">
        <div className="metric-value mb-1">
          <span 
            className={`fs-2 fw-bold ${valueColor} d-block text-truncate metric-value`} 
            title={displayValue}
            style={{ letterSpacing: '-0.01em' }}
          >
            {displayValue}
          </span>
        </div>

        {/* Real-time chart component */}
        {renderChart()}

        {/* Phase 5 Task 3: Show capacity info for SOC measurements */}
        {measurement === 'soc' && realTimeData && (
          <CapacityInfo 
            device={_device}
            soc={value}
            realTimeData={realTimeData}
            className="mt-2"
          />
        )}
      </div>
    </div>
  )
}

/**
 * Enhanced comparison function for React.memo
 * Only re-render if measurement, value, timestamp, chartData, or visibility changes occurred
 */
const arePropsEqual = (prevProps, nextProps) => {
  const {measurement} = prevProps
  
  // Only log comparison checks for debugging when needed
  const shouldDebugLog = false // Set to true for debugging
  
  if (shouldDebugLog) {
    console.warn(`MetricCard[${measurement}] arePropsEqual check:`, {
      measurement: prevProps.measurement === nextProps.measurement,
      value: prevProps.value === nextProps.value,
      timestamp: prevProps.timestamp === nextProps.timestamp,
      unit: prevProps.unit === nextProps.unit,
      showChart: prevProps.showChart === nextProps.showChart,
      hasRecentData: prevProps.hasRecentData === nextProps.hasRecentData,
      chartDataLength: {
        prev: prevProps.chartData?.length || 0,
        next: nextProps.chartData?.length || 0,
      },
    })
  }
  // Quick reference check for primitive values (be more tolerant of timestamps)
  if (
    prevProps.measurement !== nextProps.measurement ||
    prevProps.value !== nextProps.value ||
    prevProps.unit !== nextProps.unit ||
    prevProps.showChart !== nextProps.showChart ||
    prevProps.hasRecentData !== nextProps.hasRecentData ||
    prevProps.realTimeData !== nextProps.realTimeData || // Check for realTimeData changes
    prevProps.dataSource !== nextProps.dataSource
  ) {
    if (shouldDebugLog) console.warn(`MetricCard[${measurement}] PRIMITIVE PROPS CHANGED - re-rendering`)
    return false
  }
  
  // Be more tolerant of timestamp differences (within 5 seconds to reduce noise)
  if (prevProps.timestamp && nextProps.timestamp) {
    const timeDiff = Math.abs(new Date(nextProps.timestamp) - new Date(prevProps.timestamp))
    if (timeDiff > 5000) { // Only re-render if timestamp differs by more than 5 seconds
      if (shouldDebugLog) console.warn(`MetricCard[${measurement}] TIMESTAMP CHANGED SIGNIFICANTLY (${timeDiff}ms) - re-rendering`)
      return false
    }
  } else if (prevProps.timestamp !== nextProps.timestamp) {
    // One timestamp is null but the other isn't
    if (shouldDebugLog) console.warn(`MetricCard[${measurement}] TIMESTAMP CHANGED (null vs non-null) - re-rendering`)
    return false
  }
  
  // Compare chartData arrays more intelligently
  const prevChartData = prevProps.chartData || []
  const nextChartData = nextProps.chartData || []
    // If lengths are different, definitely need to re-render
  if (prevChartData.length !== nextChartData.length) {
    if (shouldDebugLog) console.warn(`MetricCard[${measurement}] CHART DATA LENGTH CHANGED (${prevChartData.length} -> ${nextChartData.length}) - re-rendering`)
    return false
  }
  
  // If both are empty, no need to re-render
  if (prevChartData.length === 0 && nextChartData.length === 0) {
    if (shouldDebugLog) console.warn(`MetricCard[${measurement}] BOTH CHART DATA EMPTY - skipping render`)
    return true
  }
  
  // Compare the last few data points for meaningful changes
  const compareCount = Math.min(3, prevChartData.length) // Check last 3 points
  for (let i = 0; i < compareCount; i++) {
    const prevIndex = prevChartData.length - 1 - i
    const nextIndex = nextChartData.length - 1 - i
    
    const prevPoint = prevChartData[prevIndex]
    const nextPoint = nextChartData[nextIndex]
    
    if (!prevPoint || !nextPoint) continue
      // Compare actual data values, not just references
    if (
      prevPoint.x !== nextPoint.x ||
      prevPoint.y !== nextPoint.y ||
      prevPoint.timestamp !== nextPoint.timestamp ||
      prevPoint.value !== nextPoint.value
    ) {
      if (shouldDebugLog) console.warn(`MetricCard[${measurement}] CHART DATA POINT CHANGED - re-rendering`)
      return false
    }
  }
  
  if (shouldDebugLog) console.warn(`MetricCard[${measurement}] PROPS EQUAL - skipping render`)
  return true // Props are functionally equal, skip re-render
}

export default React.memo(MetricCard, arePropsEqual)
